import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import Image from 'next/image'
import { Input } from 'antd'
import { useTranslation } from 'src/utils/i18n'

const ExtremeReverifyModal = ({
  open,
  loading,
  mode = 1,
  username,
  password,
  errorText,
  lockedErrorText,
  onPasswordChange,
  onClose,
  onSendCode,
  onLater,
}) => {
  const { t } = useTranslation('common')
  const [mounted, setMounted] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const titleKey = mode === 2 ? 'Extreme Data Verification' : 'iCloud Account Verification'

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    if (!open) {
      setShowPassword(false)
    }
  }, [open])

  if (!open || !mounted) return null

  const passwordHasError = Boolean(errorText || lockedErrorText)

  return createPortal(
    <div className="fixed bottom-0 left-0 right-0 top-[60px] z-[1316] flex items-center justify-center overflow-y-auto bg-[rgba(0,0,0,0.5)] px-4 py-4 smj:items-start smj:px-4 smj:pb-4 smj:pt-3 smjs:left-[300px]">
      <div className="relative my-auto flex w-full max-w-[544px] flex-col overflow-hidden rounded-[12px] bg-white shadow-[0px_6px_20px_0px_rgba(23,24,33,0.2)] smj:my-0 smj:max-h-[calc(100dvh-60px-28px)] lg:rounded-[16px]">
        <button
          type="button"
          aria-label="close"
          className="absolute right-3 top-3 z-[2] flex h-6 w-6 items-center justify-center text-[#9297AF]"
          onClick={onClose}
        >
          <span className="relative block h-4 w-4">
            <span className="absolute left-1/2 top-1/2 block h-[18px] w-[2px] -translate-x-1/2 -translate-y-1/2 rotate-45 rounded bg-current" />
            <span className="absolute left-1/2 top-1/2 block h-[18px] w-[2px] -translate-x-1/2 -translate-y-1/2 -rotate-45 rounded bg-current" />
          </span>
        </button>

        <div className="min-h-0 flex-1 overflow-y-auto p-6 lg:p-8">
          <div className="flex flex-col items-center gap-3">
            <Image src="/images/apps/apple.svg" alt="" width={60} height={69} className="h-[60px] w-[52px]" />
            <h2 className="text-center text-[20px] font-semibold leading-[28px] text-[#020417] lg:text-[24px] lg:leading-9">
              {t(titleKey)}
            </h2>
          </div>

          <div className="mt-6 flex flex-col gap-2.5">
            <div className="flex flex-col gap-2.5">
              <label className="text-[16px] font-medium leading-6 text-[#020417]">{t('Apple ID')}</label>
              <div className="flex items-center justify-between rounded-[8px] border border-[#E9EFF3] bg-[rgba(244,245,246,0.92)] px-4 py-2">
                <Input
                  bordered={false}
                  className="!p-0 text-[16px] leading-6 text-[#020417]"
                  value={username}
                  disabled
                  placeholder={t('Apple ID')}
                />
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-[16px] font-medium leading-6 text-[#020417]">{t('Password')}</label>
              <div
                className={`flex h-10 items-center justify-between rounded-[8px] border px-4 py-2 ${
                  passwordHasError ? 'border-[#FF4C4C]' : 'border-[#E9EFF3]'
                }`}
              >
                <Input
                  bordered={false}
                  type={showPassword ? 'text' : 'password'}
                  className="!p-0 text-[16px] leading-6 text-[#020417] placeholder:text-[#BBBFca]"
                  value={password}
                  onChange={e => onPasswordChange?.(e.target.value)}
                  placeholder={t('Password')}
                />
                <button
                  type="button"
                  className="ml-2 shrink-0"
                  onClick={() => setShowPassword(prev => !prev)}
                  aria-label="toggle password"
                >
                  <Image
                    src={showPassword ? '/extreme/open.svg' : '/extreme/close.svg'}
                    alt=""
                    width={24}
                    height={24}
                    className="h-6 w-6"
                  />
                </button>
              </div>
              {passwordHasError && (
                <p className="text-right text-[12px] leading-normal text-[#FF4C4C]">
                  {lockedErrorText || errorText}
                </p>
              )}
            </div>
          </div>

          <div className="mt-6 flex items-start gap-2.5 rounded-[12px] bg-[rgba(74,119,254,0.1)] p-3">
            <Image src="/images/ios-locked/info-icon.svg" alt="" width={24} height={24} className="mt-0.5 h-6 w-6 shrink-0" />
            <p className="text-[14px] leading-5 text-[#4A77FE]">
              {t('Once verified Apple will send a two-factor verification code or SMS to the monitored device Please ensure the device can receive messages')}
            </p>
          </div>

          <div className="mt-8 flex flex-col items-center gap-4">
            <button
              type="button"
              disabled={loading}
              className={`w-full rounded-[40px] bg-[#FFCF55] px-10 py-2.5 text-[14px] font-medium leading-5 text-[#020417] lg:w-[240px] ${loading ? 'opacity-70' : ''}`}
              onClick={onSendCode}
            >
              {t('Send Code')}
            </button>
            <button
              type="button"
              className="text-[14px] font-medium leading-5 text-[#1C1E53]/60"
              onClick={onLater}
            >
              {t('Later for Verification')}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}

export default ExtremeReverifyModal
